/**
 * ChangeDetailModal Component (AC-572)
 *
 * Modal that displays full details of a change including entity information,
 * a DiffViewer showing the changes, and a re-apply action button.
 *
 * Parent Component: ChangeList
 *
 * Features:
 * - Display change info (entity type icon, name, project, timestamp)
 * - Render DiffViewer with previous_state and new_state
 * - "Re-apply this change" button for update/create projects
 * - Loading state during re-apply
 * - Error handling and display
 * - Close on success, stay open on error
 * - Close button and Escape key support
 * - Accessible dialog structure
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { X, Users, Database, GitBranch, RotateCcw, Undo2, Trash2, Plus, Edit3, Loader2, ListOrdered, History } from 'lucide-react';
import { useVersionStore } from '../../store/versionStore';
import { ChangeDetail } from '../../types/version_control';
import DiffViewer from './DiffViewer';
import Tooltip from '../common/Tooltip';
import { StepInfo, StepChangesSummary, getStepDisplayName, analyzeStepChanges } from './stepChangeUtils';

/**
 * Component to display step changes summary
 */
const StepChangesSummaryDisplay: React.FC<{ stepChanges: StepChangesSummary }> = ({
  stepChanges,
}) => {
  const { added, deleted, modified } = stepChanges;

  return (
    <div className="space-y-3" data-testid="step-changes-summary">
      {/* Deleted Steps */}
      {deleted.length > 0 && (
        <div className="space-y-2">
          {deleted.map((step) => (
            <div
              key={step.orchestration_step_id}
              className="flex items-center gap-2 p-2 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg"
            >
              <Trash2 className="w-4 h-4 text-red-500 flex-shrink-0" />
              <span className="text-sm text-red-700 dark:text-red-400">
                <span className="font-medium">Deleted step:</span>{' '}
                {getStepDisplayName(step)}
              </span>
            </div>
          ))}
        </div>
      )}

      {/* Added Steps */}
      {added.length > 0 && (
        <div className="space-y-2">
          {added.map((step) => (
            <div
              key={step.orchestration_step_id}
              className="flex items-center gap-2 p-2 bg-green-50 border border-green-200 rounded-lg"
            >
              <Plus className="w-4 h-4 text-green-500 flex-shrink-0" />
              <span className="text-sm text-green-700">
                <span className="font-medium">Added step:</span>{' '}
                {getStepDisplayName(step)}
              </span>
            </div>
          ))}
        </div>
      )}

      {/* Modified Steps */}
      {modified.length > 0 && (
        <div className="space-y-2">
          {modified.map(({ before, after, changedFields }) => (
            <div
              key={before.orchestration_step_id}
              className="flex items-start gap-2 p-2 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg"
            >
              <Edit3 className="w-4 h-4 text-yellow-600 dark:text-yellow-400 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-yellow-800 dark:text-yellow-300">
                <span className="font-medium">Modified step:</span>{' '}
                {getStepDisplayName(before)}
                <div className="text-xs text-yellow-600 dark:text-yellow-400 mt-1">
                  Changed: {changedFields.join(', ')}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

/**
 * Generate smart badges for changed fields, with special handling for steps
 */
function getSmartChangedFieldBadges(
  changedFields: string[],
  stepChanges: StepChangesSummary | null
): Array<{ label: string; className: string }> {
  const badges: Array<{ label: string; className: string }> = [];

  // Handle steps specially
  if (changedFields.includes('steps') && stepChanges) {
    const { added, deleted, modified } = stepChanges;
    if (deleted.length > 0) {
      badges.push({
        label: `${deleted.length} step${deleted.length !== 1 ? 's' : ''} deleted`,
        className: 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300',
      });
    }
    if (added.length > 0) {
      badges.push({
        label: `${added.length} step${added.length !== 1 ? 's' : ''} added`,
        className: 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300',
      });
    }
    if (modified.length > 0) {
      badges.push({
        label: `${modified.length} step${modified.length !== 1 ? 's' : ''} modified`,
        className: 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300',
      });
    }
  }

  // Add other non-step fields
  changedFields
    .filter((field) => field !== 'steps')
    .forEach((field) => {
      badges.push({
        label: field,
        className: 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300',
      });
    });

  return badges;
}

interface ChangeDetailModalProps {
  isOpen: boolean;
  change: ChangeDetail | null;
  projectId: number;
  onClose: () => void;
  onReapplied?: () => void;
  onUndone?: () => void;
  /** Callback when revert project completes successfully */
  onReverted?: () => void;
  /** If true, disables Re-apply and Revert buttons since this change represents the current entity state */
  isLatestForEntity?: boolean;
}

/**
 * Format a date string for display
 */
function formatDate(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * Format relative time
 */
function formatRelativeTime(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHour = Math.floor(diffMin / 60);
  const diffDay = Math.floor(diffHour / 24);

  if (diffSec < 60) {
    return 'just now';
  } else if (diffMin < 60) {
    return `${diffMin} minute${diffMin !== 1 ? 's' : ''} ago`;
  } else if (diffHour < 24) {
    return `${diffHour} hour${diffHour !== 1 ? 's' : ''} ago`;
  } else if (diffDay === 1) {
    return 'yesterday';
  } else if (diffDay < 30) {
    return `${diffDay} day${diffDay !== 1 ? 's' : ''} ago`;
  } else {
    return formatDate(dateString);
  }
}

/**
 * Get the icon component for an entity type
 */
function EntityTypeIcon({
  entityType,
}: {
  entityType: 'agent' | 'model' | 'orchestration' | 'queue';
}) {
  switch (entityType) {
    case 'agent':
      return <Users className="w-5 h-5" data-testid="icon-agent" aria-label="agent" role="img" />;
    case 'model':
      return <Database className="w-5 h-5" data-testid="icon-model" aria-label="model" role="img" />;
    case 'orchestration':
      return <GitBranch className="w-5 h-5" data-testid="icon-workflow" aria-label="orchestration" role="img" />;
    case 'queue':
      return <ListOrdered className="w-5 h-5" data-testid="icon-queue" aria-label="queue" role="img" />;
    default:
      return null;
  }
}

/**
 * Get project badge styling
 */
function getOperationBadgeClasses(operation: string): string {
  switch (operation) {
    case 'create':
      return 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300';
    case 'update':
      return 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300';
    case 'delete':
      return 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300';
    case 'restore':
      return 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300';
    case 'reapply':
      return 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300';
    case 'undo':
      return 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300';
    case 'revert':
      return 'bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300';
    default:
      return 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300';
  }
}

const ChangeDetailModal: React.FC<ChangeDetailModalProps> = ({
  isOpen,
  change,
  projectId,
  onClose,
  onReapplied,
  onUndone,
  onReverted,
  isLatestForEntity = false,
}) => {
  const [reapplying, setReapplying] = useState(false);
  const [undoing, setUndoing] = useState(false);
  const [reverting, setReverting] = useState(false);
  const [showUndoConfirm, setShowUndoConfirm] = useState(false);
  const [showRevertConfirm, setShowRevertConfirm] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { reapplyChange, undoChange, revertToChange } = useVersionStore();

  // Reset state when modal opens/closes
  useEffect(() => {
    if (!isOpen) {
      setReapplying(false);
      setUndoing(false);
      setReverting(false);
      setShowUndoConfirm(false);
      setShowRevertConfirm(false);
      setError(null);
    }
  }, [isOpen]);

  // Handle Escape key
  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        onClose();
      }
    },
    [isOpen, onClose]
  );

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleKeyDown]);

  // Handle backdrop click
  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  // Handle re-apply
  const handleReapply = async () => {
    if (!change) return;

    setReapplying(true);
    setError(null);

    try {
      await reapplyChange(projectId, change.change_id);
      onReapplied?.();
      onClose();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to re-apply change';
      setError(message);
    } finally {
      setReapplying(false);
    }
  };

  // Handle undo click - show confirmation dialog
  const handleUndoClick = () => {
    setShowUndoConfirm(true);
  };

  // Handle undo confirm
  const handleUndoConfirm = async () => {
    if (!change) return;

    setShowUndoConfirm(false);
    setUndoing(true);
    setError(null);

    try {
      await undoChange(projectId, change.change_id);
      onUndone?.();
      onClose();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to undo change';
      setError(message);
    } finally {
      setUndoing(false);
    }
  };

  // Handle undo cancel
  const handleUndoCancel = () => {
    setShowUndoConfirm(false);
  };

  // Handle revert click - show confirmation dialog
  const handleRevertClick = () => {
    setShowRevertConfirm(true);
  };

  // Handle revert confirm
  const handleRevertConfirm = async () => {
    if (!change) return;

    setShowRevertConfirm(false);
    setReverting(true);
    setError(null);

    try {
      await revertToChange(projectId, change.change_id);
      onReverted?.();
      onClose();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to revert to version';
      setError(message);
    } finally {
      setReverting(false);
    }
  };

  // Handle revert cancel
  const handleRevertCancel = () => {
    setShowRevertConfirm(false);
  };

  // Analyze step changes for workflows
  const stepChanges = useMemo(() => {
    if (!change || change.entity_type !== 'orchestration') {
      return null;
    }
    if (!change.changed_fields?.includes('steps')) {
      return null;
    }
    return analyzeStepChanges(
      change.previous_state as Record<string, unknown> | null,
      change.new_state as Record<string, unknown> | null
    );
  }, [change]);

  // Get smart badges for changed fields
  const smartBadges = useMemo(() => {
    if (!change?.changed_fields) {
      return [];
    }
    return getSmartChangedFieldBadges(change.changed_fields, stepChanges);
  }, [change?.changed_fields, stepChanges]);

  // Don't render if not open or no change
  if (!isOpen || !change) {
    return null;
  }

  // Determine if re-apply should be shown (not for delete projects)
  // Disable if this is the latest change for the entity (re-applying would be redundant)
  const showReapply = change.operation !== 'delete';
  const reapplyDisabled = reapplying || undoing || reverting || isLatestForEntity;

  // Determine if undo should be shown (for update and delete operations)
  const showUndo = change.operation === 'update' || change.operation === 'delete';

  // Determine if revert should be shown (for create/update projects that have new_state)
  // Revert means "restore entity to the state it had at this change"
  // Not shown for delete projects since there's no state to revert to
  const showRevert = change.operation !== 'delete' && change.new_state !== null;
  const revertDisabled = reapplying || undoing || reverting || isLatestForEntity;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      onClick={handleBackdropClick}
      data-testid="modal-backdrop"
    >
      <div
        role="dialog"
        aria-labelledby="change-detail-modal-title"
        aria-modal="true"
        className="bg-white dark:bg-cap-dark rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-3">
            <div
              className={`p-2 rounded-lg ${
                change.entity_type === 'agent'
                  ? 'bg-blue-100 dark:bg-blue-900/30 text-primary-600 dark:text-primary-400'
                  : change.entity_type === 'model'
                  ? 'bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400'
                  : 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400'
              }`}
              data-testid="entity-icon"
            >
              <EntityTypeIcon entityType={change.entity_type} />
            </div>
            <div>
              <h2
                id="change-detail-modal-title"
                className="text-xl font-semibold text-gray-900 dark:text-warm-white"
              >
                {change.entity_name}
              </h2>
              <div className="flex items-center gap-2 mt-1">
                <span className="text-sm text-gray-500 dark:text-gray-400 capitalize">
                  {change.entity_type}
                </span>
                <span className="text-gray-300">|</span>
                <span
                  className={`text-xs px-2 py-0.5 rounded font-medium ${getOperationBadgeClasses(
                    change.operation
                  )}`}
                >
                  {change.operation}
                </span>
              </div>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:text-gray-400 transition-colors"
            aria-label="Exit"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Timestamp and metadata */}
          <div className="flex items-center justify-between text-sm text-gray-500 dark:text-gray-400">
            <span>{formatRelativeTime(change.created_at)}</span>
            <span>{formatDate(change.created_at)}</span>
          </div>

          {/* Changed fields list with smart badges */}
          {smartBadges.length > 0 && (
            <div>
              <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Changed Fields
              </h3>
              <div className="flex flex-wrap gap-2">
                {smartBadges.map((badge, index) => (
                  <span
                    key={`${badge.label}-${index}`}
                    className={`px-2 py-1 text-xs rounded font-medium ${badge.className}`}
                  >
                    {badge.label}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Checkpoint reference */}
          {change.checkpoint_id && (
            <div className="text-sm text-gray-500 dark:text-gray-400">
              <span className="font-medium">Associated checkpoint:</span>{' '}
              <span>#{change.checkpoint_id}</span>
            </div>
          )}

          {/* Metadata section */}
          {change.metadata && Object.keys(change.metadata).length > 0 && (
            <div data-testid="change-metadata">
              <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Metadata</h3>
              <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-3 text-sm">
                {Object.entries(change.metadata).map(([key, value]) => (
                  <div key={key} className="flex gap-2">
                    <span className="text-gray-500 dark:text-gray-400">{key}:</span>
                    <span className="text-gray-900 dark:text-warm-white">{String(value)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Step changes summary for workflows */}
          {stepChanges && (
            <div>
              <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Step Changes</h3>
              <StepChangesSummaryDisplay stepChanges={stepChanges} />
            </div>
          )}

          {/* Diff viewer - hide if only step changes for workflows */}
          {(!stepChanges || (change.changed_fields && change.changed_fields.filter(f => f !== 'steps' && f !== 'updated_at' && f !== 'created_at').length > 0)) && (
            <div>
              <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                {stepChanges ? 'Other Changes' : 'Changes'}
              </h3>
              <DiffViewer
                previousState={change.previous_state as Record<string, unknown> | null}
                newState={change.new_state as Record<string, unknown> | null}
                project={
                  change.operation === 'create' || change.operation === 'delete'
                    ? change.operation
                    : 'update'
                }
              />
            </div>
          )}

          {/* Error message */}
          {error && (
            <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200 dark:border-gray-700">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-cap-dark border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 dark:bg-gray-900 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            aria-label="Close"
          >
            Close
          </button>
          {showUndo && !showUndoConfirm && !showRevertConfirm && (
            <Tooltip content="Reverts this specific change by restoring the previous state">
              <button
                type="button"
                onClick={handleUndoClick}
                disabled={undoing || reapplying || reverting}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg hover:bg-amber-100 dark:hover:bg-amber-900/40 focus:ring-2 focus:ring-amber-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
                aria-label="Undo this change"
              >
                {undoing ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Undo2 className="w-4 h-4" />
                )}
                {undoing ? 'Undoing...' : 'Undo'}
              </button>
            </Tooltip>
          )}
          {showRevert && !showUndoConfirm && !showRevertConfirm && (
            <Tooltip content={isLatestForEntity ? 'This is already the current state' : 'Restores the entity to exactly this state, undoing all changes made after this point'}>
              <button
                type="button"
                onClick={handleRevertClick}
                disabled={revertDisabled}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-indigo-700 bg-indigo-50 border border-indigo-200 rounded-lg hover:bg-indigo-100 focus:ring-2 focus:ring-indigo-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
                aria-label={isLatestForEntity ? 'Revert to this version disabled (already current state)' : 'Revert to this version'}
              >
                {reverting ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <History className="w-4 h-4" />
                )}
                {reverting ? 'Reverting...' : 'Revert to this version'}
              </button>
            </Tooltip>
          )}
          {showReapply && !showUndoConfirm && !showRevertConfirm && (
            <Tooltip content={isLatestForEntity ? 'This is already the current state' : 'Applies only the fields changed in this update to the current entity state'}>
              <button
                type="button"
                onClick={handleReapply}
                disabled={reapplyDisabled}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-primary-600 border border-transparent rounded-lg hover:bg-primary-700 focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
                aria-label={isLatestForEntity ? 'Re-apply disabled (already current state)' : 'Re-apply this change'}
              >
                {reapplying ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <RotateCcw className="w-4 h-4" />
                )}
                {reapplying ? 'Applying...' : 'Re-apply'}
              </button>
            </Tooltip>
          )}
        </div>

        {/* Undo Confirmation Dialog */}
        {showUndoConfirm && (
          <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center rounded-lg">
            <div className="bg-white dark:bg-cap-dark rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-warm-white mb-2">
                Confirm Undo Change
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
                This will revert the entity to its previous state. A new change
                record will be created to track this undo project.
              </p>
              <div className="flex justify-end gap-3">
                <button
                  onClick={handleUndoCancel}
                  className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                  aria-label="Cancel"
                >
                  Cancel
                </button>
                <button
                  onClick={handleUndoConfirm}
                  className="px-4 py-2 text-sm font-medium text-white bg-amber-600 rounded-lg hover:bg-amber-700 transition-colors"
                  aria-label="Confirm undo"
                >
                  Undo Change
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Revert Confirmation Dialog */}
        {showRevertConfirm && (
          <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center rounded-lg">
            <div className="bg-white dark:bg-cap-dark rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-warm-white mb-2">
                Confirm Revert to Version
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
                Are you sure you want to revert to this version? This will undo all changes
                made after this point and restore the entity to the state it had at this change.
                A new change record will be created to track this revert project.
              </p>
              <div className="flex justify-end gap-3">
                <button
                  onClick={handleRevertCancel}
                  className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                  aria-label="Cancel"
                >
                  Cancel
                </button>
                <button
                  onClick={handleRevertConfirm}
                  className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 transition-colors"
                  aria-label="Confirm revert"
                >
                  Revert to Version
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ChangeDetailModal;
